import { OnlyOfficeEditor } from "@/components/common/OnlyOfficeEditor.tsx";
import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { mimes } from "@/constants/mimes.ts";
import type { CaseActivity } from "@/features/case-evaluation/types/activities";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import type { Media } from "@/features/dashboard/types/media.ts";
import { formatDate } from "@/utils/format-date";
import { formatFileSize } from "@/utils/format-file-size.ts";
import { IconAlertCircle, IconEye, IconFile, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { ScrollArea } from "../core/scroller";
import { PdfViewer } from "../pdf-viewer";

export default function CaseActivityPreviewModal({ data, title }: { data: CaseActivity; title: string }) {
	const { closeModal } = useModalControl();
	const documentTokenMutation = useDocumentConfigMutation();
	const [document, setDocument] = useState<Media | null>(data.attachments?.length !== 0 && data.attachments ? data?.attachments[0] : null);
	const [docOpts, setDocOpts] = useState({
		token: "",
		options: {},
	});

	useEffect(() => {
		if (data.attachments && data.attachments.length !== 0) {
			documentTokenMutation
				.mutateAsync({
					file_type: "docx",
					title: document?.file_name || "Document Name",
					mode: "view",
					url: document?.original_url,
				})
				.then((response) => {
					setDocOpts({
						token: response.token,
						options: response.options,
					});
				});
		}
	}, [data, document]);

	return (
		<div className="flex flex-col w-full h-full">
			<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
				<Panel defaultSize={30} minSize={30} maxSize={30} className="flex flex-col h-full min-w-[420px] max-w-[420px]">
					<div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
						<span className="text-base font-bold text-foreground-dark max-w-[500px] truncate">{title}</span>
						<button
							onClick={() => {
								closeModal();
							}}
							className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
							type="button"
						>
							<IconX size={20} stroke={1.5} />
						</button>
					</div>

					<ScrollArea className="h-[calc(100svh - 120px)]">
						<div className="flex flex-col w-full p-4 gap-4">
							{data?.with_fees && (
								<div className="col-span-12 w-full">
									<Alert type="info" allowClose={false} title="This Court Action has fees" icon={<IconAlertCircle size={20} stroke={2} />} />
								</div>
							)}
							<div className="flex flex-col w-full gap-2">
								<div className="flex flex-col w-full">
									<span className="text-base text-foreground/70">Title</span>
									<span className="text-base font-semibold text-foreground-dark">{data.activity}</span>
								</div>
								<div className="flex flex-col w-full">
									<span className="text-base text-foreground/70">Recipient</span>
									<span className="text-base font-semibold text-foreground-dark">{data.assigned_to}</span>
								</div>
								{data?.with_deadline && (
									<div className="flex flex-col w-full">
										<span className="text-base text-foreground/70">Deadline</span>
										<span className="text-base font-semibold text-foreground-dark">{formatDate(data.due_at, "PPp")}</span>
									</div>
								)}
								{data?.service_date && (
									<div className="flex flex-col w-full">
										<span className="text-base text-foreground/70">Service Date</span>
										<span className="text-base font-semibold text-foreground-dark">{formatDate(data.service_date, "PP")}</span>
									</div>
								)}
								<div className="flex flex-col w-full">
									<span className="text-base text-foreground/70">Remarks</span>
									<span className="text-base font-semibold text-foreground-dark">{data.remarks || "N/A"}</span>
								</div>
								{data?.status && (
									<div className="flex flex-col w-full">
										<span className="text-base text-foreground/70">Status</span>
										<span className="text-base font-semibold text-foreground-dark">{data.status}</span>
									</div>
								)}
								{data?.sheriff_attempts.map((attempt, index) => (
									<div key={index} className="flex flex-col w-full">
										<span className="text-base text-foreground/70">Sheriff Attempt {index + 1}</span>
										<span className="text-base font-semibold text-foreground-dark">{formatDate(attempt?.attempted_at, "PPp")}</span>
									</div>
								))}

								<div className="flex flex-col w-full">
									<span className="text-base text-foreground/70">Author</span>
									<span className="text-base font-semibold text-foreground-dark">{data.author?.full_name}</span>
								</div>
								<div className="flex flex-col w-full">
									<span className="text-base text-foreground/70">Created At</span>
									<span className="text-base font-semibold text-foreground-dark">{data.created_at}</span>
								</div>
							</div>
						</div>
						<div className="flex flex-col w-full px-4 gap-1 border-t border-border">
							<span className="py-2 font-bold">Upload Files</span>
							{data?.attachments?.map((file, index) => (
								<div key={index} className="flex flex-row justify-between items-center">
									<div className="items-center flex flex-row gap-2">
										<div className="inline-flex shrink-0 justify-center items-center w-[28px] h-[28px] rounded-md bg-slate-200 text-foreground overflow-hidden">
											<IconFile size={18} stroke={2} />
										</div>
										<div className="inline-flex flex-col">
											<span className="text-sm font-semibold text-foreground-dark">{file.name}</span>
											<span className="text-sm text-foreground/50">{formatFileSize(file.size)}</span>
										</div>
									</div>

									<div className="">
										<button
											className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md hover:text-danger-500"
											type="button"
											onClick={() => {
												setDocument(file);
											}}
										>
											<IconEye size={18} stroke={2} />
										</button>
									</div>
								</div>
							))}
						</div>
					</ScrollArea>

					<div className="flex shrink-0 flex-row items-center w-full h-[70px] px-4 gap-2 mt-auto border-t border-slate-200">
						<Button
							className="w-full"
							variant="ghost"
							type="button"
							onClick={() => {
								closeModal();
							}}
						>
							Close
						</Button>
					</div>
				</Panel>

				<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
					<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
				</PanelResizeHandle>

				<Panel defaultSize={70} minSize={70} maxSize={70} className="flex flex-col shrink grow w-full h-full bg-slate-100">
					{document?.mime_type === mimes.MSWORD && <OnlyOfficeEditor token={docOpts.token} options={docOpts.options} />}

					{document?.mime_type !== mimes.MSWORD && <PdfViewer attachment={document} file={document?.original_url} />}
				</Panel>
			</PanelGroup>
		</div>
	);
}
